/** Axial extractor fan, structured so a future system can spin only its blades. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'extractor-fan';
  const housingMaterial = surface('metal', 0x838f90);
  // An open four-sided housing is more expensive than a grey square, but retains
  // the circular rotor silhouette from oblique views. A solid backing made the
  // fan disappear into its own shadow during the first workbench composition.
  for (const [x, y, width, height, name] of [
    [0, 0.665, 0.7, 0.07, 'housing-top'], [0, 0.035, 0.7, 0.07, 'housing-bottom'],
    [-0.315, 0.35, 0.07, 0.63, 'housing-left'], [0.315, 0.35, 0.07, 0.63, 'housing-right'],
  ] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.12), housingMaterial);
    bar.name = name; bar.position.set(x, y, 0); bar.castShadow = true; group.add(bar);
  }
  const shroud = new THREE.Mesh(new THREE.TorusGeometry(0.27, 0.035, 6, 12), surface('metal', 0xadb6b4));
  shroud.name = 'round-shroud'; shroud.position.set(0, 0.35, 0.07); shroud.castShadow = true; group.add(shroud);
  const shutter = new THREE.Group(); shutter.name = 'shutter'; shutter.position.z = -0.07;
  for (let i = -2; i <= 2; i++) {
    const vane = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.025, 0.018), surface('metal', 0x697374));
    vane.name = `shutter-vane-${i + 3}`; vane.position.y = 0.35 + i * 0.095; shutter.add(vane);
  }
  group.add(shutter);
  const blades = new THREE.Group();
  blades.name = 'fan-blades';
  blades.position.set(0, 0.35, 0.11);
  for (let i = 0; i < 5; i++) {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.25, 0.022), surface('metal', 0x9ea8a7));
    blade.name = `blade-${i + 1}`;
    blade.position.y = 0.135;
    blade.rotation.z = -0.32;
    const pivot = new THREE.Group(); pivot.name = `blade-pivot-${i + 1}`; pivot.rotation.z = (i * Math.PI * 2) / 5; pivot.add(blade);
    blades.add(pivot);
  }
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.04, 10), surface('metal', 0x717c7d));
  hub.name = 'fan-hub'; hub.rotation.x = Math.PI / 2; hub.position.set(0, 0.35, 0.13); group.add(hub, blades);
  // The rotor necessarily projects beyond the shutter. Recentring the open
  // assembly, rather than filling the back with a plenum, preserves its blade
  // gaps in silhouette mode; the opaque plenum made the fan read as a cabinet.
  for (const child of group.children) child.position.z -= 0.035;
  return group;
}

export const extractorFanAsset: AssetDefinition = {
  id: 'extractor-fan', label: 'Extractor fan', description: '0.7 m axial fan in a square housing with shutter and separately named five-blade rotor.',
  footprint: { width: 0.7, height: 0.7, length: 0.23 }, create: build,
};
